import { Injectable, inject, signal } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { LandingAssistantService } from './landing-assistant.service';
import type {
  LandingAssistantAction,
  LandingAssistantMessageDto,
} from '../models/landing-assistant/landing-assistant.models';

const MAX_HISTORY_MESSAGES = 12;

export type ChatDisplayMessage = LandingAssistantMessageDto;

@Injectable({ providedIn: 'root' })
export class LandingAssistantStore {
  private readonly assistant = inject(LandingAssistantService);

  readonly isOpen = signal(false);
  readonly messages = signal<ChatDisplayMessage[]>([]);
  readonly sending = signal(false);
  readonly lastAction = signal<LandingAssistantAction>('none');
  readonly errorMessage = signal<string | null>(null);

  toggleOpen(): void {
    this.isOpen.update((open) => !open);
  }

  /** Clears the last AI-driven action (e.g. after the visitor submitted a lead). */
  clearAction(): void {
    this.lastAction.set('none');
  }

  /** Appends an assistant-authored message to the transcript without an HTTP round trip. */
  addAssistantMessage(content: string): void {
    this.messages.update((list) => [...list, { role: 'assistant', content }]);
  }

  /**
   * Attempts to send `content`. Returns `true` when the message was accepted (and a
   * request was issued), `false` when it was rejected for being empty/whitespace or
   * because another send is still in flight — callers use this to decide whether it is
   * safe to clear their draft.
   */
  sendMessage(content: string, locale: string): boolean {
    const trimmed = content.trim();
    if (!trimmed || this.sending()) {
      return false;
    }

    this.messages.update((list) => [...list, { role: 'user', content: trimmed }]);
    this.errorMessage.set(null);
    this.sending.set(true);

    const history = this.messages()
      .slice(-MAX_HISTORY_MESSAGES)
      .map(({ role, content }) => ({ role, content }));

    this.assistant
      .sendMessage({ messages: history, locale })
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe({
        next: (response) => {
          this.messages.update((list) => [...list, { role: 'assistant', content: response.reply }]);
          this.lastAction.set(response.action);
        },
        error: () => {
          this.errorMessage.set('LANDING.ASSISTANT.ERROR');
        },
      });

    return true;
  }
}
